<template>
  <SlHeader
    :current-step="2"
    :links="headerLinks"
  />

  <sl-loader
    v-if="isLoading"
    :message="'Chargement en cours ...'"
  />

  <div
    v-else
    data-cy="page-tarificateur-vigeo"
  >
    <sl-separator />

    <div class="h2 p-4">
      <div
        class="pb-4"
        data-cy="product"
      >
        SwissLife Santé Retraités - {{ selectedCombinaison }}
      </div>
      <div class="d-flex align-items-center justify-content-between">
        <cotisation-mensuelle
          :value="currentMonthlyDue"
          class=""
        />
        <sl-button
          v-if="demarchage.consent === '1' || combinaisonChanged || !canSign"
          data-cy="action-submit"
          @click="submit()"
        >
          Enregistrer cette simulation
        </sl-button>
      </div>
    </div>

    <sl-separator />

    <notice-help />

    <sl-separator />

    <sl-pricer
      ref="slPricer"
      :refund-example-tables="refundExampleTables"
      :warranty-tables="warrantyTables"
      :current-positions-selected="currentPositionsSelected"
      :range="range"
      @cell-click="onCellClick($event)"
    >
      <sl-table
        :tag="'prices'"
        :columns="pricesTable.columns"
        :datas="pricesTable.datas"
        :current-position-selected="currentPricePositionSelected"
        :range="range"
        data-cy="prices"
        @cell-click="onPriceCellClick($event)"
      />
    </sl-pricer>

    <div
      v-if="consent === '0'"
      id="consent"
      class="p-4"
    >
      <div class="row align-items-center">
        <span>Besoin du conseil d’un expert pour trouver la meilleure solution garanties/tarif ? J’accepte qu’un conseiller Swiss Life m’appelle pour m’aider.</span>
        <demarchage-telephonique
          v-model:consent="demarchage.consent"
          v-model:telephone="demarchage.telephone"
          :server-errors="serverErrors"
        />
      </div>
    </div>

    <Transition>
      <div
        v-show="consent === '1' || demarchage.consent === '1'"
        class="p-4"
      >
        <infos-agent
          :agent="agent"
        />
      </div>
    </Transition>

    <div class="h2 p-4">
      <div class="d-flex align-items-center justify-content-between">
        <cotisation-mensuelle
          :value="currentMonthlyDue"
          class=""
        />
        <sl-button
          v-if="demarchage.consent === '1' || combinaisonChanged || !canSign"
          data-cy="action-submit"
          @click="submit()"
        >
          Enregistrer cette simulation
        </sl-button>
      </div>
    </div>

    <sl-downloads :downloads="downloads" />

    <sl-separator class="my-4" />

    <mentions-vigeo
      :mentions="mentions"
      :renvois="renvois"
      data-cy="mentions"
    />
  </div>
</template>

<script>
import { onMounted, ref, reactive, toRefs, computed, watch, nextTick } from 'vue'
import SlDownloads from 'src/modules/shared/components/SlDownloads';
import SlButton from "src/modules/shared/components/SlButton";
import SlLoader from "src/modules/shared/components/SlLoader";
import SlPricer from "src/modules/shared/components/SlPricer";
import SlTable from 'src/modules/shared/components/Table/SlTable';
import SlSeparator from "src/modules/shared/components/SlSeparator";
import TarificationVigeoService from "src/modules/tarificateur/services/tarification-vigeo.service";
import mockPrices from "src/modules/tarificateur/__fixtures__/getPricesResponse__vigeo";
import mockParameters from "src/modules/tarificateur/__fixtures__/getParametersResponse__vigeo.json";
import { formatString } from "src/modules/shared/utils/utils";
import { convertVigeoPricesResponseIntoTable} from 'src/modules/tarificateur/datatransformers/vigeo-to-table.datatransformer'
import CotisationMensuelle from "src/modules/tarificateur/components/CotisationMensuelle"
import DemarchageTelephonique from "src/modules/tarificateur/components/DemarchageTelephonique"
import InfosAgent from "src/modules/tarificateur/components/InfosAgent"
import { rowColToCombinaison, combinaisonToRowCol} from "src/modules/shared/utils/combinaisonUtils";
import {convertDateFromYYYYMMDDinDDMMYYYY} from "src/modules/shared/utils/dateUtils"
import { convertWarrantyTableResponseIntoTables} from 'src/modules/tarificateur/datatransformers/warranty-to-table.datatransformer'
import { convertRefundExampleResponseIntoTables } from 'src/modules/tarificateur/datatransformers/refund-example-to-table.datatransformer'
import useDialog from "src/modules/shared/composable/useDialog";
import SlHeader from "src/modules/shared/components/SlHeader";
import NoticeHelp from "src/modules/tarificateur/components/NoticeHelp";
import MentionsVigeo from "src/modules/tarificateur/components/MentionsVigeo";
import {useVuelidate} from "@vuelidate/core";
import smoothscroll from "smoothscroll-polyfill";

export default {
  name: "TarificateurVigeo",
  components: {
    CotisationMensuelle,
    DemarchageTelephonique,
    InfosAgent,
    NoticeHelp,
    MentionsVigeo,
    SlButton,
    SlDownloads,
    SlHeader,
    SlLoader,
    SlPricer,
    SlSeparator,
    SlTable
  },
  emits: [
    'goto'
  ],
  setup(props, {emit}) {
    const initialState = window.__INITIAL_STATE__ || {}
    smoothscroll.polyfill()
    const withElectronicSignature = ref(initialState.withElectronicSignature ?? true)
    const fromBusinessFinder = ref(initialState.fromBusinessFinder ?? false)

    const mock = false
    const content = ref('')
    const slPricer = ref(null)

    const { showLoaderSending, showPopinError, hideLoader, showPopin} = useDialog()

    // l'écart +/- autour de la sélection, à récupérer plus tard d'un ws
    const range = 8

    const renvois = ref('')
    const mentions = ref('')
    const prices = ref('')
    const devoirDeConseilUrl = ref('')
    const parameters = ref('')
    const dateEffet = ref('')
    const originalCombinaison = ref('')

    const state = reactive({
      // ui
      isLoading: true,
      loaded: false,
      isTabExample: true,
      // tables
      currentPricePositionSelected: {col:3, row:0},
      currentPositionsSelected: [],
      currentMonthlyDue: '00,00 €',
      //data
      pricesTable: {
        columns: [],
        datas: []
      },
      warrantyTables: [],
      refundExampleTables: [],
      downloads: [],
      consent: '0',
      canSign: false,
      combinaisonChanged: ''
    })

    const demarchage = reactive({
      consent: '',
      telephone: '',
    })

    const agent = reactive({
      libelleAgent: '',
      adresseAgent: '',
      telAgent: '',
      emailAgent: '',
      numOriasAgent: '',
    })

    const serverErrors = ref([])

    const selectedCombinaison = computed(() => {
      return rowColToCombinaison(state.currentPricePositionSelected.row, state.currentPricePositionSelected.col)
    })

    const warrantyTablePdfUrl = computed(() => {
      return TarificationVigeoService.returnWarrantyTablePdfUrl(selectedCombinaison.value)
    })

    const selectedCombinaisonWatcher = watch(
        [()=> selectedCombinaison.value],
        () => {
          if (state.downloads[0]) {
            state.downloads[0].link = warrantyTablePdfUrl.value
          }
        }
    )

    const headerLinks = computed(() => [
        devoirDeConseilUrl.value
    ])


    const scrollToElement = (id) => {
      const el = document.getElementById(id)
      if (el) {
        el.scrollIntoView({
          behavior: 'auto',
          block: 'center',
          inline: 'center'
        })
      }
    }

    const onSentQuotation = async () => {
      console.debug('[TarificateurVigeo]', 'onSentQuotation')

      const data = {
        combinaison: selectedCombinaison.value,
        dateEffet: convertDateFromYYYYMMDDinDDMMYYYY(dateEffet.value)
      }

      console.debug("[TarificateurVigeo]", "submitForm", data)

      await showLoaderSending("#app")
      scrollToElement('sl-dialog-loader')

      TarificationVigeoService.submitForm(data).then(res => {
        console.debug("[TarificateurVigeo]", "submitForm", "res", res)
        showPopin('', 'Votre demande a bien été prise en compte.', '#app')

        if (!withElectronicSignature.value || fromBusinessFinder.value) {
          emit('goto', 'souscription-vigeo')
        }

      }).catch(error => {
        console.error("[TarificateurVigeo]", "submit", "error", error.response.data.msg)
        const errorMsg = error.response.data.msg ?? 'Erreur serveur'
        showPopinError(errorMsg)
      }).finally(() => {
        hideLoader()
      })
    }

    const checkCurrentPricePositionAndUpdatePrice = () => {
      if (selectedCombinaison.value === 'H1D1M1P1S1') {
        state.currentPricePositionSelected.row = 0
      }
      state.currentMonthlyDue = prices.value[selectedCombinaison.value]
    }

    const onPriceCellClick = (cell) => {
      const tag = cell.tag
      const position = cell.position
      const value = cell.value

      console.debug('[TarificateurVigeo]', 'onPriceCellClick', 'tag', tag, 'position', position, 'value', value)
      // row à undefined indique un header (cas spécial)
      if (position.col !== 0 && typeof position.row !== 'undefined' && value.label !== '-') {
        state.currentPricePositionSelected = position
        state.currentPositionsSelected = generatePositionsSelected(position, [...state.warrantyTables, ...state.refundExampleTables])
        checkCurrentPricePositionAndUpdatePrice()
      }

      if (position.col !== 0 && typeof position.row === 'undefined') {
        state.currentPricePositionSelected.col = position.col
        state.currentPositionsSelected = generatePositionsSelected(position, [...state.warrantyTables, ...state.refundExampleTables])
        checkCurrentPricePositionAndUpdatePrice()
      }
      if (originalCombinaison.value !== selectedCombinaison.value) {
        state.combinaisonChanged = state.currentPositionsSelected
      } else {
        state.combinaisonChanged = ''
      }
    }

    const onCellClick = (cell) => {
      const tag = cell.tag
      const position = cell.position
      const value = cell.value

      if (cell.position.col === 0) return

      console.debug('[TarificateurVigeo]', 'onCellClick', 'tag', tag, 'position', position, 'value', value)
      state.currentPricePositionSelected.col = position.col
      state.currentPositionsSelected = generatePositionsSelected(position, [...state.warrantyTables, ...state.refundExampleTables])
      checkCurrentPricePositionAndUpdatePrice()
      if (originalCombinaison.value !== selectedCombinaison.value) {
        state.combinaisonChanged = state.currentPositionsSelected
      } else {
        state.combinaisonChanged = ''
      }
    }

    /**
     * Génère une liste des positions pour chaque tableau par rapport à une position de référence
     * @param {*} position la position dans le tableau: {col:0,row:0}
     * @param {*} tables la liste des tableaux pour générer la liste de position: [{label:'Dentaire', columns:{}, data:{}}]
     */
    const generatePositionsSelected = (position, tables) => {
      // console.debug('[SlPricer]','generatePositionsSelected', 'position', JSON.stringify(position), 'tables', JSON.stringify(tables))
      const positions = [];
      // en omettant la valeur row, on indique que la selection se fait sur la colonne
      const newPosition = {col: position.col, row: undefined}
      // on considère que le nombre de tableau dans les exemples et les valeurs est le même
      tables.forEach(table => {
        const key = table.label
        positions[key] = newPosition
      })

      return positions
    }

    onMounted(async () => {

      if (mock) {
        // on mocke la réponse en attendant les ws
        renvois.value = mockParameters.renvois
        mentions.value = mockParameters.mentions

        prices.value = mockPrices
        parameters.value = mockParameters
        originalCombinaison.value = 'H1D1M1P1S0'
        dateEffet.value = '2021-08-08'
      } else {

        await TarificationVigeoService.getUserInformations().then(res => {
          state.consent = res.data.consent

          agent.libelleAgent = res.data.libelleAgent
          agent.adresseAgent = res.data.adresseAgent
          agent.villeAgent = res.data.villeAgent
          agent.cpAgent = res.data.cpAgent
          agent.telAgent = res.data.telAgent
          agent.emailAgent = res.data.emailAgent
          agent.numOriasAgent = res.data.numOriasAgent

          state.canSign = res.data.canSign
          prices.value = res.data.prices
          originalCombinaison.value = res.data.combinaison
          dateEffet.value = res.data.dateEffet
          devoirDeConseilUrl.value = res.data.devoirDeConseilUrl

          const combinaisonInRowCol = combinaisonToRowCol(originalCombinaison.value)
          state.currentPricePositionSelected = combinaisonInRowCol
        })

        await TarificationVigeoService.getParameters(dateEffet.value).then(res => {
          parameters.value = res.data
          renvois.value = res.data.renvois
          mentions.value = res.data.mentions
        })
      }

      // on crée les liens de téléchargement à partir de la réponse
      const downloads = [
        {label:'Tableau de garanties détaillé', link: warrantyTablePdfUrl.value},
        {label:'Exemples de remboursements', link: parameters.value.exemplesRemboursementsPdf},
        {label:'Notice d\'information', link: parameters.value.noticeInformationsPdf}
      ]
      state.downloads = downloads

      // on convertit le retour du webservice de prix en tableau
      const { columns: columnsPrices, datas: dataPrices, numberOfLevel } = convertVigeoPricesResponseIntoTable(prices.value, formatString, onPriceCellClick)
      state.pricesTable = {
        columns: columnsPrices,
        datas: dataPrices
      }

      // On convertit le retour du webservice de vigeo en tableau
      const { warrantyTables } = convertWarrantyTableResponseIntoTables(parameters.value.tableauGaranties, formatString, numberOfLevel)
      state.warrantyTables = warrantyTables

      const { refundExampleTables } = convertRefundExampleResponseIntoTables(parameters.value.exemplesRemboursements, formatString, numberOfLevel)
      state.refundExampleTables = refundExampleTables

      // on calcule une liste de colonne sélectionnée par défaut pour chaque tableau
      state.currentPricePositionSelected = combinaisonToRowCol(originalCombinaison.value)
      state.currentPositionsSelected = generatePositionsSelected(
          state.currentPricePositionSelected,
          [...state.warrantyTables, ...state.refundExampleTables]
      )

      // On affiche la cotisation annuelle
      state.currentMonthlyDue = state.pricesTable.datas[state.currentPricePositionSelected.row][state.currentPricePositionSelected.col].label

      // On informe que tout s'est bien passé
      state.loaded = true
      state.isLoading = false

      /**
       * Récupère le "message" contenant le type de tableau à afficher par défaut et force l'affichage de celui-ci
       */
      window.addEventListener("message", function(event) {
        if(event.data.action === 'navigate') {
          // On met à jour l'iframe
          let iframe = document.querySelector('#iframe');
          iframe.src = event.data.url;
        }

        if (event.data && event.data.key === 'tableau_par_default') {
          console.debug('[TarificateurVigeo]', 'message', event)
          // 0 => exemple de remboursement
          // 1 => tableau de garanties
          slPricer.value.onTabClick(event.data.value === '0')
        }
      });
    })

    return {
      v$: useVuelidate(),
      onSentQuotation,
      scrollToElement,
      range,
      ...toRefs(state),
      content,
      dateEffet,
      demarchage,
      agent,
      hideLoader,
      originalCombinaison,
      selectedCombinaison,
      warrantyTablePdfUrl,
      selectedCombinaisonWatcher,
      serverErrors,
      showLoaderSending,
      showPopinError,
      onCellClick,
      onPriceCellClick,
      slPricer,
      headerLinks,
      renvois,
      mentions,
      withElectronicSignature,
      fromBusinessFinder
    }
  },
  methods: {
    async submit() {

      // On reset les erreurs serveurs
      this.serverErrors = []
      await nextTick()

      // On valide les formulaires
      this.v$.$touch()
      console.debug('VUE TOUCH', this.v$)

      // Hack : On regarde si on a uniquement des erreurs serveurs car elles ne partent pas d'elles-mêmes...
      let onlyServerError = true
      for (const error of this.v$.$errors) {
        if (error.$validator !== 'serverError') onlyServerError = false
      }

      // En cas d'erreur, on ne fait rien (les composants vont afficher d'eux-mêmes les erreurs)
      if (this.v$.$error && !onlyServerError) {
        // Si le slug correspond au nom du model, le scroll s'effectue bien vers la première erreur
        this.scrollToElement(this.v$.$errors[0].$property)
        return
      }

      // Début des envois
      await this.showLoaderSending("#app")

      if (this.consent === '0') {
        TarificationVigeoService.updateConsentement(this.demarchage).then(res => {
          console.debug("[TarificateurVigeo]", "updateConsentement", "res", res)
        }).catch(error => {
          console.error("[TarificateurVigeo]", "submit", "error", error.response.data.msg)

          // Si on détecte des erreurs serveurs, on met la variable à jour
          if (error.response.data.violations !== undefined) {
            this.serverErrors = error.response.data.violations
            console.error(this.serverErrors)
          } else {
            const errorMsg = error.response.data.msg ?? 'Erreur serveur'
            this.showPopinError(errorMsg)
          }
        })
      }

      // Si on ne peut pas signer ou combinaison/demarchage qui change
      // On fait l'enregistrement
      if (!this.canSign || this.demarchage.consent === '1' || this.combinaisonChanged) {
        this.onSentQuotation()
        this.originalCombinaison = this.combinaisonChanged
        this.combinaisonChanged = ''
      }
    }
  }
}
</script>

<style scoped>
.v-enter-active,
.v-leave-active {
  transition: opacity .5s ease;
}
.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
